import React, { Suspense } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from "react-router-dom";
import { RouteErrorFallback } from "../components/RouteErrorFallback";
import { isChunkLoadError, tryReloadOnceForStaleChunks } from "../lib/chunk-load-recovery";
import { AppRoot, OpsHostRedirect } from "./AppRoot";
import { GuestRoute, ProtectedRoute } from "../components/ProtectedRoute";
import { RequireRole } from "../components/RequireRole";
import { DashboardPage } from "../pages/dashboard/DashboardPage";
import { LoginPage } from "../pages/LoginPage";
import { PaywallPage } from "../pages/PaywallPage";
import { PlatformAdminPage } from "../pages/ops/PlatformAdminPage";
import { PlatformSheetPage } from "../pages/ops/PlatformSheetPage";
import { ProtectedLayout } from "./ProtectedLayout";

// Lazy-loaded components (code-split)
const CrmDashboardPage = () => import("../features/crm").then(m => ({ default: m.CrmDashboardPage }));
const GlDashboardPage = () => import("../features/gl").then(m => ({ default: m.GlDashboardPage }));
const HrDashboardPage = () => import("../features/hr").then(m => ({ default: m.HrDashboardPage }));
const InventoryDashboardPage = () => import("../features/inventory").then(m => ({ default: m.InventoryDashboardPage }));
const PurchasesDashboardPage = () => import("../features/purchases").then(m => ({ default: m.PurchasesDashboardPage }));
const VendorPurchaseDetailReportPage = () =>
  import("../features/purchases/pages/VendorPurchaseDetailReportPage").then((m) => ({
    default: m.VendorPurchaseDetailReportPage,
  }));
const PurchaseBillDetailPage = () => import("../pages/purchases/PurchaseBillDetailPage");
const SalesDashboardPage = () => import("../features/sales").then(m => ({ default: m.SalesDashboardPage }));
const ForbiddenPage = () => import("../pages/errors/ForbiddenPage");
const FadvisorPage = () => import("../pages/fadvisor/FadvisorPage");
const ReportsPage = () => import("../pages/reports/ReportsPage");
const PeriodActivitySummaryPage = () =>
  import("../pages/reports/PeriodActivitySummaryPage");
const TrialBalanceMonthlyPage = () =>
  import("../pages/reports/TrialBalanceMonthlyPage");
const PaymentBudgetReportPage = () =>
  import("../pages/reports/PaymentBudgetReportPage");
const NetWorthStatementPage = () => import("../pages/reports/NetWorthStatementPage");
const AiAdvisorPage = () => import("../pages/ai/AiAdvisorPage");
const ChartOfAccountsPage = () => import("../pages/coa/ChartOfAccountsPage");
const AgingPage = () => import("../pages/aging/AgingPage");
const InventoryValuationPage = () => import("../features/inventory/pages/InventoryValuationPage");
const StockCountPage = () => import("../features/inventory/pages/StockCountPage");
const BankReconPage = () => import("../pages/bank-recon/BankReconPage");
const VatReturnPage = () => import("../pages/vat-return/VatReturnPage");
const ZakatReportPage = () => import("../pages/zakat/ZakatReportPage");
const PartyStatementPage = () => import("../pages/party-statement/PartyStatementPage");
const JournalEntriesPage = () => import("../pages/journal-entries/JournalEntriesPage");
const CashVouchersPage = () => import("../pages/finance/CashVouchersPage");
const ChequesPage = () => import("../pages/finance/ChequesPage");
const CashFlowPage = () => import("../pages/cash-flow/CashFlowPage");
const AccountingPeriodsPage = () => import("../pages/periods/AccountingPeriodsPage");
const ProfitabilityPage = () => import("../pages/profitability/ProfitabilityPage");
const ZktecoRoute = () => import("../pages/zkteco/ZktecoRoute");
const PosPage = () => import("../pages/pos/PosPage");
const ProductionBomDashboardPage = () => import("../features/production/pages/ProductionBomDashboardPage");
const ProductionHubPage = () => import("../features/production/pages/ProductionHubPage");
const ProductionRunFlowPage = () => import("../features/production/pages/ProductionRunFlowPage");
const ProductionRunsDashboardPage = () => import("../features/production/pages/ProductionRunsDashboardPage");
const RoutingFlowPage = () => import("../features/production/pages/RoutingFlowPage");
const RoutingsDashboardPage = () => import("../features/production/pages/RoutingsDashboardPage");
const ProductionWorkOrdersDashboardPage = () => import("../features/production/pages/ProductionWorkOrdersDashboardPage");
const ProductionBomFormPage = () => import("../pages/production/ProductionBomFormPage");
const ProductionWorkOrderDetailPage = () => import("../pages/production/ProductionWorkOrderDetailPage");
const ManufacturingPricingEnginePage = () =>
  import("../features/production/pages/ManufacturingPricingEnginePage");
const ProductionRequestReportPage = () =>
  import("../features/production/pages/ProductionRequestReportPage");
const MfgProductivityReportPage = () =>
  import("../features/production/pages/MfgProductivityReportPage");
const MfgVarianceReportPage = () =>
  import("../features/production/pages/MfgVarianceReportPage");
const DepreciationRunPage = () => import("../pages/fixed-assets/DepreciationRunPage");
const FixedAssetFormPage = () => import("../pages/fixed-assets/FixedAssetFormPage");
const FixedAssetsPage = () => import("../pages/fixed-assets/FixedAssetsPage");
const SalesInvoiceDetailPage = () => import("../pages/sales/SalesInvoiceDetailPage");
const EtaSettingsPage = () => import("../pages/settings/EtaSettingsPage");
const GlobalSettingsForm = () => import("../pages/settings/GlobalSettingsForm");
const CompanyBrandingPage = () =>
  import("../pages/settings/CompanyBrandingPage").then((m) => ({
    default: m.CompanyBrandingPage,
  }));
const SettingsLayout = () => import("../pages/settings/SettingsLayout");
const ZatcaSettingsPage = () => import("../pages/settings/ZatcaSettingsPage");
const ShopifyIntegrationPage = () => import("../pages/settings/shopify/ShopifyIntegrationPage");
const StoreIntegrationsHubPage = () => import("../pages/settings/stores/StoreIntegrationsHubPage");
const LocalEngineSettingsPage = () => import("../pages/settings/LocalEngineSettingsPage");
const AttendancePage = () => import("../pages/hr/attendance/AttendancePage");
const PayrollPage = () => import("../pages/payroll/PayrollPage");
const PayrollSettingsPage = () => import("../pages/payroll/PayrollSettingsPage");
const LeaveManagementPage = () => import("../pages/payroll/LeaveManagementPage");
const EmployeePayslipReportPage = () =>
  import("../pages/payroll/EmployeePayslipReportPage");
const ShippingCodPage = () => import("../pages/shipping/ShippingCodPage");
const ItemBalanceReportPage = () => import("../features/inventory/pages/ItemBalanceReportPage");
const InventoryMovementSummaryPage = () =>
  import("../features/inventory/pages/InventoryMovementSummaryPage");
const SlowMovingStockReportPage = () =>
  import("../features/inventory/pages/SlowMovingStockReportPage");
const ExpiryReportPage = () => import("../features/inventory/pages/ExpiryReportPage");
const ReorderReportPage = () => import("../features/inventory/pages/ReorderReportPage");
const ProductionPointReportPage = () => import("../features/inventory/pages/ProductionPointReportPage");
const InventoryAnomaliesPage = () => import("../features/inventory/pages/InventoryAnomaliesPage");
const ItemCardReportPage = () => import("../features/inventory/pages/ItemCardReportPage");
const ItemClassificationPage = () => import("../features/inventory/pages/ItemClassificationPage");
const CommissionReportPage = () => import("../features/sales/pages/CommissionReportPage");
const SalesMiniReportsPage = () => import("../pages/sales/SalesMiniReportsPage");
const CustomerBalancesReportPage = () =>
  import("../features/sales/pages/CustomerBalancesReportPage");
const DataMigrationPage = () => import("../pages/DataMigrationPage");
const SmartImporterHubPage = () => import("../pages/SmartImporterHubPage");
const DirectoriesPage = () => import("../pages/directories/DirectoriesPage");

// Simple loading fallback for suspense
const LazyFallback = () => (
  <div className="erp-page">
    <div className="erp-panel flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-godo-accent"></div>
    </div>
  </div>
);

// Wrapper for lazy components with Suspense
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = React.ComponentType<any>;

const withLazy = (
  loader: () => Promise<Record<string, AnyComponent>>,
): AnyComponent => {
  const LazyComponent = React.lazy(async () => {
    try {
      const m = await loader();
      if (m && typeof m === "object" && "default" in m && m.default) return { default: m.default };
      const keys = Object.keys(m);
      const first = keys[0] ? m[keys[0]] : undefined;
      if (first && typeof first === "function") return { default: first };
      return { default: (() => null) as AnyComponent };
    } catch (err) {
      if (isChunkLoadError(err)) tryReloadOnceForStaleChunks();
      throw err;
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (props: any) => (
    <Suspense fallback={<LazyFallback />}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

// Wrap lazy components
const LazyCrmDashboardPage = withLazy(CrmDashboardPage);
const LazyGlDashboardPage = withLazy(GlDashboardPage);
const LazyHrDashboardPage = withLazy(HrDashboardPage);
const LazyInventoryDashboardPage = withLazy(InventoryDashboardPage);
const LazyPurchasesDashboardPage = withLazy(PurchasesDashboardPage);
const LazyPurchaseBillDetailPage = withLazy(PurchaseBillDetailPage);
const LazyVendorPurchaseDetailReportPage = withLazy(VendorPurchaseDetailReportPage);
const LazySalesDashboardPage = withLazy(SalesDashboardPage);
const LazyForbiddenPage = withLazy(ForbiddenPage);
const LazyFadvisorPage = withLazy(FadvisorPage);
const LazyReportsPage = withLazy(ReportsPage);
const LazyPeriodActivitySummaryPage = withLazy(PeriodActivitySummaryPage);
const LazyTrialBalanceMonthlyPage = withLazy(TrialBalanceMonthlyPage);
const LazyPaymentBudgetReportPage = withLazy(PaymentBudgetReportPage);
const LazyNetWorthStatementPage = withLazy(NetWorthStatementPage);
const LazyAiAdvisorPage = withLazy(AiAdvisorPage);
const LazyChartOfAccountsPage = withLazy(ChartOfAccountsPage);
const LazyAgingPage = withLazy(AgingPage);
const LazyInventoryValuationPage = withLazy(InventoryValuationPage);
const LazyStockCountPage = withLazy(StockCountPage);
const LazyBankReconPage = withLazy(BankReconPage);
const LazyVatReturnPage = withLazy(VatReturnPage);
const LazyZakatReportPage = withLazy(ZakatReportPage);
const LazyPartyStatementPage = withLazy(PartyStatementPage);
const LazyJournalEntriesPage = withLazy(JournalEntriesPage);
const LazyCashVouchersPage = withLazy(CashVouchersPage);
const LazyChequesPage = withLazy(ChequesPage);
const LazyCashFlowPage = withLazy(CashFlowPage);
const LazyAccountingPeriodsPage = withLazy(AccountingPeriodsPage);
const LazyProfitabilityPage = withLazy(ProfitabilityPage);
const LazyZktecoRoute = withLazy(ZktecoRoute);
const LazyPosPage = withLazy(PosPage);
const LazyProductionBomDashboardPage = withLazy(ProductionBomDashboardPage);
const LazyProductionHubPage = withLazy(ProductionHubPage);
const LazyProductionRunFlowPage = withLazy(ProductionRunFlowPage);
const LazyProductionRunsDashboardPage = withLazy(ProductionRunsDashboardPage);
const LazyRoutingFlowPage = withLazy(RoutingFlowPage);
const LazyRoutingsDashboardPage = withLazy(RoutingsDashboardPage);
const LazyProductionWorkOrdersDashboardPage = withLazy(ProductionWorkOrdersDashboardPage);
const LazyProductionBomFormPage = withLazy(ProductionBomFormPage);
const LazyProductionWorkOrderDetailPage = withLazy(ProductionWorkOrderDetailPage);
const LazyManufacturingPricingEnginePage = withLazy(ManufacturingPricingEnginePage);
const LazyProductionRequestReportPage = withLazy(ProductionRequestReportPage);
const LazyMfgProductivityReportPage = withLazy(MfgProductivityReportPage);
const LazyMfgVarianceReportPage = withLazy(MfgVarianceReportPage);
const LazyDepreciationRunPage = withLazy(DepreciationRunPage);
const LazyFixedAssetFormPage = withLazy(FixedAssetFormPage);
const LazyFixedAssetsPage = withLazy(FixedAssetsPage);
const LazySalesInvoiceDetailPage = withLazy(SalesInvoiceDetailPage);
const LazyEtaSettingsPage = withLazy(EtaSettingsPage);
const LazyGlobalSettingsForm = withLazy(GlobalSettingsForm);
const LazyCompanyBrandingPage = withLazy(CompanyBrandingPage);
const LazySettingsLayout = withLazy(SettingsLayout);
const LazyZatcaSettingsPage = withLazy(ZatcaSettingsPage);
const LazyShopifyIntegrationPage = withLazy(ShopifyIntegrationPage);
const LazyStoreIntegrationsHubPage = withLazy(StoreIntegrationsHubPage);
const LazyLocalEngineSettingsPage = withLazy(LocalEngineSettingsPage);
const LazyAttendancePage = withLazy(AttendancePage);
const LazyPayrollPage = withLazy(PayrollPage);
const LazyPayrollSettingsPage = withLazy(PayrollSettingsPage);
const LazyLeaveManagementPage = withLazy(LeaveManagementPage);
const LazyEmployeePayslipReportPage = withLazy(EmployeePayslipReportPage);
const LazyShippingCodPage = withLazy(ShippingCodPage);
const LazyItemBalanceReportPage = withLazy(ItemBalanceReportPage);
const LazyInventoryMovementSummaryPage = withLazy(InventoryMovementSummaryPage);
const LazySlowMovingStockReportPage = withLazy(SlowMovingStockReportPage);
const LazyExpiryReportPage = withLazy(ExpiryReportPage);
const LazyReorderReportPage = withLazy(ReorderReportPage);
const LazyProductionPointReportPage = withLazy(ProductionPointReportPage);
const LazyInventoryAnomaliesPage = withLazy(InventoryAnomaliesPage);
const LazyItemCardReportPage = withLazy(ItemCardReportPage);
const LazyItemClassificationPage = withLazy(ItemClassificationPage);
const LazyCommissionReportPage = withLazy(CommissionReportPage);
const LazySalesMiniReportsPage = withLazy(SalesMiniReportsPage);
const LazyCustomerBalancesReportPage = withLazy(CustomerBalancesReportPage);
const LazyDataMigrationPage = withLazy(DataMigrationPage);
const LazySmartImporterHubPage = withLazy(SmartImporterHubPage);
const LazyDirectoriesPage = withLazy(DirectoriesPage);
const BillingHubPage = () =>
  import("../pages/billing/BillingHubPage").then((m) => ({
    default: m.BillingHubPage,
  }));
const LazyBillingHubPage = withLazy(BillingHubPage);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppRoot />} errorElement={<RouteErrorFallback />}>
      <Route path="/sheet" element={<PlatformSheetPage />} />
      <Route path="/platform" element={<PlatformAdminPage />} />
      <Route path="/ops/platform" element={<PlatformAdminPage />} />
      <Route path="/ops/sheet" element={<PlatformSheetPage />} />

      <Route element={<OpsHostRedirect />}>
      <Route path="/pay" element={<PaywallPage />} />

          <Route element={<GuestRoute />}>
            <Route path="/login" element={<LoginPage />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route element={<ProtectedLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/forbidden" element={<LazyForbiddenPage />} />

              <Route
                path="/gl"
                element={
                  <RequireRole moduleId="gl">
                    <LazyGlDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="/coa"
                element={
                  <RequireRole moduleId="coa">
                    <LazyChartOfAccountsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/aging"
                element={
                  <RequireRole moduleId="aging">
                    <LazyAgingPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inventory-valuation"
                element={
                  <RequireRole moduleId="inventory_valuation">
                    <LazyInventoryValuationPage />
                  </RequireRole>
                }
              />
              <Route
                path="/stock-count"
                element={
                  <RequireRole moduleId="stock_count">
                    <LazyStockCountPage />
                  </RequireRole>
                }
              />
              <Route
                path="/bank-recon"
                element={
                  <RequireRole moduleId="bank_recon">
                    <LazyBankReconPage />
                  </RequireRole>
                }
              />
              <Route
                path="/vat-return"
                element={
                  <RequireRole moduleId="vat_return">
                    <LazyVatReturnPage />
                  </RequireRole>
                }
              />
              <Route
                path="/zakat"
                element={
                  <RequireRole moduleId="zakat">
                    <LazyZakatReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/party-statement"
                element={
                  <RequireRole moduleId="party_statement">
                    <LazyPartyStatementPage />
                  </RequireRole>
                }
              />
              <Route
                path="/journal-entries"
                element={
                  <RequireRole moduleId="journal_entries">
                    <LazyJournalEntriesPage />
                  </RequireRole>
                }
              />
              <Route
                path="/finance/cash-vouchers"
                element={
                  <RequireRole moduleId="gl">
                    <LazyCashVouchersPage />
                  </RequireRole>
                }
              />
              <Route
                path="/finance/cheques"
                element={
                  <RequireRole moduleId="gl">
                    <LazyChequesPage />
                  </RequireRole>
                }
              />
              <Route
                path="/cash-flow"
                element={
                  <RequireRole moduleId="cash_flow">
                    <LazyCashFlowPage />
                  </RequireRole>
                }
              />
              <Route
                path="/accounting-periods"
                element={
                  <RequireRole moduleId="accounting_periods">
                    <LazyAccountingPeriodsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/profitability"
                element={
                  <RequireRole moduleId="profitability">
                    <LazyProfitabilityPage />
                  </RequireRole>
                }
              />
              <Route path="/zkteco" element={<LazyZktecoRoute />} />
              <Route
                path="/settings"
                element={
                  <RequireRole moduleId="settings">
                    <LazySettingsLayout />
                  </RequireRole>
                }
              >
                <Route index element={<LazyGlobalSettingsForm />} />
                <Route path="company" element={<LazyCompanyBrandingPage />} />
                <Route path="einvoice" element={<LazyEtaSettingsPage />} />
                <Route path="zatca" element={<LazyZatcaSettingsPage />} />
                <Route path="stores" element={<LazyStoreIntegrationsHubPage />} />
                <Route path="stores/shopify" element={<LazyShopifyIntegrationPage />} />
                <Route path="local-engine" element={<LazyLocalEngineSettingsPage />} />
                <Route
                  path="shopify"
                  element={<Navigate to="/settings/stores/shopify" replace />}
                />
              </Route>
              <Route
                path="/purchases"
                element={
                  <RequireRole moduleId="purchases">
                    <LazyPurchasesDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="/purchases/bills/:billId"
                element={
                  <RequireRole moduleId="purchases">
                    <LazyPurchaseBillDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/purchases/reports/vendor-detail"
                element={
                  <RequireRole moduleId="purchases">
                    <LazyVendorPurchaseDetailReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/fixed-assets"
                element={
                  <RequireRole moduleId="fixed_assets">
                    <LazyFixedAssetsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/fixed-assets/new"
                element={
                  <RequireRole moduleId="fixed_assets">
                    <LazyFixedAssetFormPage />
                  </RequireRole>
                }
              />
              <Route
                path="/fixed-assets/depreciation-run"
                element={
                  <RequireRole moduleId="fixed_assets">
                    <LazyDepreciationRunPage />
                  </RequireRole>
                }
              />
              <Route
                path="/fixed-assets/:assetId/edit"
                element={
                  <RequireRole moduleId="fixed_assets">
                    <LazyFixedAssetFormPage />
                  </RequireRole>
                }
              />

              <Route
                path="/hr"
                element={
                  <RequireRole moduleId="hr">
                    <LazyHrDashboardPage />
                  </RequireRole>
                }
              />
              <Route path="/hr/employees" element={<Navigate to="/hr" replace />} />
              <Route
                path="/hr/attendance"
                element={
                  <RequireRole moduleId="attendance">
                    <LazyAttendancePage />
                  </RequireRole>
                }
              />
              <Route
                path="/payroll"
                element={
                  <RequireRole moduleId="payroll">
                    <LazyPayrollPage />
                  </RequireRole>
                }
              />
              <Route
                path="/payroll/settings"
                element={
                  <RequireRole moduleId="payroll_settings">
                    <LazyPayrollSettingsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/payroll/leaves"
                element={
                  <RequireRole moduleId="payroll">
                    <LazyLeaveManagementPage />
                  </RequireRole>
                }
              />
              <Route
                path="/payroll/payslip"
                element={
                  <RequireRole moduleId="payroll">
                    <LazyEmployeePayslipReportPage />
                  </RequireRole>
                }
              />

              <Route
                path="/inventory"
                element={
                  <RequireRole moduleId="inventory">
                    <LazyInventoryDashboardPage />
                  </RequireRole>
                }
              />

              <Route
                path="/sales"
                element={
                  <RequireRole moduleId="sales">
                    <LazySalesDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="/sales/invoices/:invoiceId"
                element={
                  <RequireRole moduleId="sales">
                    <LazySalesInvoiceDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/pos"
                element={
                  <RequireRole moduleId="pos">
                    <LazyPosPage />
                  </RequireRole>
                }
              />
              <Route
                path="/pos/restaurant"
                element={
                  <RequireRole moduleId="pos">
                    <LazyPosPage />
                  </RequireRole>
                }
              />

              <Route
                path="/crm"
                element={
                  <RequireRole moduleId="crm">
                    <LazyCrmDashboardPage />
                  </RequireRole>
                }
              />

              <Route
                path="/shipping"
                element={
                  <RequireRole moduleId="shipping">
                    <LazyShippingCodPage />
                  </RequireRole>
                }
              />

              <Route
                path="/production"
                element={
                  <RequireRole moduleId="production">
                    <LazyProductionHubPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/bom"
                element={
                  <RequireRole moduleId="production">
                    <LazyProductionBomDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/bom/new"
                element={<Navigate to="/production/bom" replace />}
              />
              <Route
                path="/production/bom/:bomId/edit"
                element={
                  <RequireRole moduleId="production">
                    <LazyProductionBomFormPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/work-orders"
                element={
                  <RequireRole moduleId="production">
                    <LazyProductionWorkOrdersDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/work-orders/new"
                element={<Navigate to="/production/work-orders" replace />}
              />
              <Route
                path="/production/work-orders/:workOrderId"
                element={
                  <RequireRole moduleId="production">
                    <LazyProductionWorkOrderDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/pricing-engine"
                element={
                  <RequireRole moduleId="production">
                    <LazyManufacturingPricingEnginePage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/reports/production-request"
                element={
                  <RequireRole moduleId="production">
                    <LazyProductionRequestReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/reports/productivity"
                element={
                  <RequireRole moduleId="production">
                    <LazyMfgProductivityReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/reports/variance"
                element={
                  <RequireRole moduleId="production">
                    <LazyMfgVarianceReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/routings"
                element={
                  <RequireRole moduleId="production">
                    <LazyRoutingsDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/routings/:routingId/flow"
                element={
                  <RequireRole moduleId="production">
                    <LazyRoutingFlowPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/runs"
                element={
                  <RequireRole moduleId="production">
                    <LazyProductionRunsDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="/production/runs/:runId"
                element={
                  <RequireRole moduleId="production">
                    <LazyProductionRunFlowPage />
                  </RequireRole>
                }
              />
              <Route
                path="/bom"
                element={<Navigate to="/production" replace />}
              />

              <Route
                path="/fadvisor"
                element={
                  <RequireRole moduleId="fadvisor">
                    <LazyFadvisorPage />
                  </RequireRole>
                }
              />

              <Route
                path="/reports"
                element={
                  <RequireRole moduleId="reports">
                    <LazyReportsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/reports/period-activity"
                element={
                  <RequireRole moduleId="reports">
                    <LazyPeriodActivitySummaryPage />
                  </RequireRole>
                }
              />
              <Route
                path="/reports/trial-balance-monthly"
                element={
                  <RequireRole moduleId="reports">
                    <LazyTrialBalanceMonthlyPage />
                  </RequireRole>
                }
              />
              <Route
                path="/reports/payment-budget"
                element={
                  <RequireRole moduleId="reports">
                    <LazyPaymentBudgetReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/reports/net-worth"
                element={
                  <RequireRole moduleId="reports">
                    <LazyNetWorthStatementPage />
                  </RequireRole>
                }
              />

              <Route
                path="/ai"
                element={
                  <RequireRole moduleId="ai">
                    <LazyAiAdvisorPage />
                  </RequireRole>
                }
              />

              <Route
                path="/inventory/reports/item-balance"
                element={
                  <RequireRole moduleId="inventory">
                    <LazyItemBalanceReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inventory/reports/movement-summary"
                element={
                  <RequireRole moduleId="inventory">
                    <LazyInventoryMovementSummaryPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inventory/reports/slow-moving"
                element={
                  <RequireRole moduleId="inventory">
                    <LazySlowMovingStockReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inventory/reports/expiry"
                element={
                  <RequireRole moduleId="inventory">
                    <LazyExpiryReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inventory/reports/reorder"
                element={
                  <RequireRole moduleId="inventory">
                    <LazyReorderReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inventory/reports/production-point"
                element={
                  <RequireRole moduleId="inventory">
                    <LazyProductionPointReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inventory/anomalies"
                element={
                  <RequireRole moduleId="inventory">
                    <LazyInventoryAnomaliesPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inventory/classification"
                element={
                  <RequireRole moduleId="inventory">
                    <LazyItemClassificationPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inventory/reports/item-card"
                element={
                  <RequireRole moduleId="inventory">
                    <LazyItemCardReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/sales/reports/statement"
                element={
                  <Navigate to="/party-statement" replace />
                }
              />
              <Route
                path="/sales/reports/commission"
                element={
                  <RequireRole moduleId="sales">
                    <LazyCommissionReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/sales/reports/mini"
                element={
                  <RequireRole moduleId="sales">
                    <LazySalesMiniReportsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/sales/reports/customer-balances"
                element={
                  <RequireRole moduleId="sales">
                    <LazyCustomerBalancesReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/sales/reports/aging"
                element={
                  <Navigate to="/aging" replace />
                }
              />

              <Route
                path="/directories"
                element={
                  <RequireRole moduleId="directories">
                    <LazyDirectoriesPage />
                  </RequireRole>
                }
              />
              <Route
                path="/data-migration"
                element={
                  <RequireRole moduleId="data_migration">
                    <LazyDataMigrationPage />
                  </RequireRole>
                }
              />
              <Route
                path="/data-migration/smart-importer"
                element={
                  <RequireRole moduleId="data_migration">
                    <LazySmartImporterHubPage />
                  </RequireRole>
                }
              />

              <Route
                path="/billing"
                element={
                  <RequireRole moduleId="billing">
                    <LazyBillingHubPage />
                  </RequireRole>
                }
              />
            </Route>
          </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Route>
  ),
);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
